import React, { useCallback } from "react";

import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import Announcement from "@mapmycustomers/shared/types/announcements/Announcement";
import { Button } from "@mapmycustomers/ui";

import styles from "./AnnouncementFooter.module.scss";
import Pager from "./Pager";

const messages = defineMessages({
  announcementFooterDone: {
    id: "announcements.announcementFooter.doneButton.label",
    defaultMessage: "Done",
    description: "Label of the button that closes the last announcement",
  },
  announcementFooterNext: {
    id: "announcements.announcementFooter.nextButton.label",
    defaultMessage: "Next",
    description: "Label of the button that moves to the next announcement",
  },
  announcementFooterRemindLater: {
    id: "announcements.announcementFooter.remindLaterButton.label",
    defaultMessage: "Remind me later",
    description: "Label of the button that postpones the current announcement",
  },
});

interface AnnouncementFooterProps {
  announcement: Announcement;
  currentIndex: number;
  onNext: (announcement: Announcement) => void;
  onRemindLater: (announcement: Announcement) => void;
  total: number;
}

const AnnouncementFooter = ({
  announcement,
  currentIndex,
  onNext,
  onRemindLater,
  total,
}: AnnouncementFooterProps) => {
  const intl = useIntl();

  const handleRemindLater = useCallback(
    () => onRemindLater(announcement),
    [announcement, onRemindLater]
  );
  const handleNext = useCallback(() => onNext(announcement), [announcement, onNext]);

  const isLast = currentIndex === total - 1;

  return (
    <div className={styles.container}>
      <Button className={styles.remindLaterButton} onClick={handleRemindLater} type="control">
        {intl.formatMessage(messages.announcementFooterRemindLater)}
      </Button>
      <Pager current={currentIndex} total={total} />
      <Button
        className={styles.nextButton}
        onClick={handleNext}
        rightIcon={isLast ? undefined : <FontAwesomeIcon icon={faChevronRight} />}
        type="text"
      >
        {intl.formatMessage(
          isLast ? messages.announcementFooterDone : messages.announcementFooterNext
        )}
      </Button>
    </div>
  );
};

export default AnnouncementFooter;
